/**
 * The passenger's flight, for a driver standing in an arrivals hall.
 *
 * The number alone was already on the trip (Facts' "Járat"), and it is the
 * one thing that could not answer the question the driver actually has: is
 * the plane late, and by how much. So this card leads with the delay — the
 * "delayed meter" — and carries the timetable arrival and the time to
 * believe underneath it.
 *
 * Everything comes from the trip the API last returned: `flight_status`
 * (portalViews.helper), refreshed by the trip screen's own five-minute poll.
 * Nothing is computed here and nothing is fetched here — a delay this screen
 * invented would move the free-waiting clock, which is the last thing it
 * should do.
 *
 * A flight the API could not look up says so plainly rather than showing
 * "Időben", which would be a guess dressed as a fact — but only on a trip it
 * actually asked about (an ARRIVING passenger, `free_waiting.airport`). On a
 * transfer TO the airport nobody looked, and claiming a failed lookup would
 * be its own small lie.
 *
 * `stale` is the same honesty about the OTHER failure: the times are here,
 * they just could not be refreshed (portalFormat's flightDataStale). They
 * stay on screen — a driver standing at the barrier is better served by a
 * slightly old arrival than by a spinner where it was — with one line
 * saying not to read them as current.
 */
import React from 'react';
import { formatTime } from '../../../Utils/timezone';
import {
  FLIGHT_COPY, flightBadge, flightDelayMinutes, flightStatus, isAirportPickupTrip,
} from '../portalFormat';
import { PlaneIcon } from './Icons';

export default function FlightCard({ trip, stale = false, copy = FLIGHT_COPY }) {
  const status = flightStatus(trip);
  const number = trip?.flight_number || status?.flight_number;
  if (!number) return null;

  const delay = flightDelayMinutes(trip);
  const badge = flightBadge(trip, copy);
  const rows = status ? [
    [copy.arrivalAirport, status.arrival_airport],
    [copy.scheduled, status.scheduled_arrival && formatTime(status.scheduled_arrival)],
    [copy.expected, status.expected_arrival && formatTime(status.expected_arrival)],
  ].filter(([, value]) => Boolean(value)) : [];

  return (
    <section
      className={`pp-card pp-flight${delay ? ' pp-flight--late' : ''}`}
      aria-label={copy.title}
      data-testid="portal-flight"
    >
      <div className="pp-flight__head">
        <PlaneIcon size={16} />
        <span className="pp-eyebrow">{copy.title}</span>
      </div>

      <div className="pp-flight__row">
        <span className="pp-flight__number">{number}</span>
        {badge ? (
          <span
            className={`pp-badge pp-badge--${badge.tone}`}
            data-testid="portal-flight-delay"
          >
            {badge.text}
          </span>
        ) : null}
      </div>

      {status ? (
        <dl className="pp-flight__times">
          {rows.map(([key, value]) => (
            <div className="pp-flight__time" key={key}>
              <dt>{key}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* Only where we really did ask. The API looks a flight up for an
          ARRIVING passenger; on a transfer to the airport there is nothing
          to report, and "we could not read it" would be untrue. */}
      {!status && isAirportPickupTrip(trip) ? (
        <p className="pp-flight__unknown">{copy.unknown}</p>
      ) : null}

      {/* Only where there is something to go stale: a card showing nothing
          but the number has no arrival to misread. */}
      {stale && status ? (
        <p className="pp-flight__unknown" data-testid="portal-flight-stale">{copy.stale}</p>
      ) : null}
    </section>
  );
}
